import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  FileText, Plus, Edit2, Trash2, X, Eye, EyeOff,
  BookOpen, Shield, Clock, Users, CheckCircle,
  AlertTriangle, Search, Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  Heading1, Heading2, Minus, RotateCcw, RotateCw, Type
} from "lucide-react";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const CATEGORIES = [
  "Leave Policy","Attendance Policy","Code of Conduct",
  "Offer Letter Terms","Termination Policy","Anti-Harassment Policy",
  "Data Privacy Policy","IT & Security Policy","Travel & Expense Policy",
  "Performance Review Policy","Grievance Policy","General Policy",
];

const CAT_ICONS = {
  "Leave Policy":"🏖️","Attendance Policy":"⏰","Code of Conduct":"📋",
  "Offer Letter Terms":"📄","Termination Policy":"🚪","Anti-Harassment Policy":"🛡️",
  "Data Privacy Policy":"🔒","IT & Security Policy":"💻","Travel & Expense Policy":"✈️",
  "Performance Review Policy":"⭐","Grievance Policy":"📣","General Policy":"📌",
};

/* Maps each category to a theme token key (resolved via T[key] at render time) */
const CAT_COLOR_KEY = {
  "Leave Policy":             "purple",
  "Attendance Policy":        "cyan",
  "Code of Conduct":          "amber",
  "Offer Letter Terms":       "green",
  "Termination Policy":       "red",
  "Anti-Harassment Policy":   "purple",
  "Data Privacy Policy":      "green",
  "IT & Security Policy":     "cyan",
  "Travel & Expense Policy":  "amber",
  "Performance Review Policy":"amber",
  "Grievance Policy":         null, // no theme token — fixed accent below
  "General Policy":           "muted",
};

const EMPTY = {
  title:"", category:"Leave Policy", content:"", summary:"",
  version:"1.0", effective_date:"", is_active:true, is_mandatory:false
};

/* ── Toolbar button ── */
const TBtn = ({onClick, active, title, children, danger=false}) => {
  const { T } = useTheme();
  return (
    <button
      type="button"
      title={title}
      onMouseDown={e=>{e.preventDefault();onClick();}}
      style={{
        width:30, height:28, display:"flex", alignItems:"center", justifyContent:"center",
        border:`1px solid ${active?`${T.cyan}66`:T.border}`,
        borderRadius:5, cursor:"pointer", transition:"all .15s",
        background: active ? `${T.cyan}1F` : "transparent",
        color: danger ? T.red : active ? T.cyan : T.muted,
      }}>
      {children}
    </button>
  );
};

const TDivider = () => {
  const { T } = useTheme();
  return (
    <div style={{ width:1, height:20, background:T.border, margin:"0 3px" }}/>
  );
};

/* ── Rich text toolbar ── */
function EditorToolbar({ editorRef }) {
  const { T } = useTheme();
  const [state, setState] = useState({});

  const update = useCallback(() => {
    setState({
      bold:        document.queryCommandState("bold"),
      italic:      document.queryCommandState("italic"),
      underline:   document.queryCommandState("underline"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter:document.queryCommandState("justifyCenter"),
      justifyRight:document.queryCommandState("justifyRight"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList:   document.queryCommandState("insertOrderedList"),
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", update);
    return () => document.removeEventListener("selectionchange", update);
  }, [update]);

  const cmd = (command, value=null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    update();
  };

  const insertHR = () => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, "<hr/><br/>");
  };

  const groups = [
    [
      { icon:<Heading1 size={13}/>, cmd:()=>cmd("formatBlock","h2"), title:"Heading 1" },
      { icon:<Heading2 size={13}/>, cmd:()=>cmd("formatBlock","h3"), title:"Heading 2" },
      { icon:<Type size={12}/>,     cmd:()=>cmd("formatBlock","p"),  title:"Paragraph" },
    ],
    [
      { icon:<Bold size={13}/>,      cmd:()=>cmd("bold"),      title:"Bold (Ctrl+B)",      key:"bold" },
      { icon:<Italic size={13}/>,    cmd:()=>cmd("italic"),    title:"Italic (Ctrl+I)",    key:"italic" },
      { icon:<Underline size={13}/>, cmd:()=>cmd("underline"), title:"Underline (Ctrl+U)", key:"underline" },
    ],
    [
      { icon:<AlignLeft size={13}/>,   cmd:()=>cmd("justifyLeft"),   title:"Align Left",   key:"justifyLeft" },
      { icon:<AlignCenter size={13}/>, cmd:()=>cmd("justifyCenter"), title:"Align Center", key:"justifyCenter" },
      { icon:<AlignRight size={13}/>,  cmd:()=>cmd("justifyRight"),  title:"Align Right",  key:"justifyRight" },
    ],
    [
      { icon:<List size={13}/>,        cmd:()=>cmd("insertUnorderedList"), title:"Bullet List",    key:"insertUnorderedList" },
      { icon:<ListOrdered size={13}/>, cmd:()=>cmd("insertOrderedList"),   title:"Numbered List",  key:"insertOrderedList" },
      { icon:<Minus size={13}/>,       cmd:()=>insertHR(),                 title:"Horizontal Rule" },
    ],
    [
      { icon:<RotateCcw size={12}/>, cmd:()=>cmd("undo"), title:"Undo (Ctrl+Z)" },
      { icon:<RotateCw size={12}/>,  cmd:()=>cmd("redo"), title:"Redo (Ctrl+Y)" },
    ],
  ];

  return (
    <div style={{
      display:"flex", alignItems:"center", gap:3, flexWrap:"wrap",
      padding:"7px 10px", background:T.card2,
      border:`1px solid ${T.border}`, borderBottom:"none",
      borderRadius:"8px 8px 0 0",
    }}>
      {groups.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <TDivider/>}
          {group.map((btn, bi) => (
            <TBtn key={bi} onClick={btn.cmd} title={btn.title}
              active={btn.key ? !!state[btn.key] : false}>
              {btn.icon}
            </TBtn>
          ))}
        </React.Fragment>
      ))}

      {/* Font size select */}
      <TDivider/>
      <select
        onChange={e=>{ editorRef.current?.focus(); document.execCommand("fontSize",false,e.target.value); }}
        title="Font size"
        style={{
          height:28, padding:"0 6px", fontSize:11, border:`1px solid ${T.border}`,
          borderRadius:5, background:T.inp_bg, color:T.muted, outline:"none",
          fontFamily:"Outfit,sans-serif", cursor:"pointer",
        }}>
        {["1","2","3","4","5"].map(s=>(
          <option key={s} value={s} style={{background:T.opt_bg}}>{["10","13","16","18","24"][s-1]}px</option>
        ))}
      </select>

      {/* Color picker */}
      <TDivider/>
      <div style={{ display:"flex", gap:4, alignItems:"center" }}>
        {[T.text, T.cyan, T.amber, T.green, T.red, T.purple, T.muted].map(c=>(
          <button key={c} type="button" title={`Color ${c}`}
            onMouseDown={e=>{e.preventDefault(); editorRef.current?.focus(); document.execCommand("foreColor",false,c);}}
            style={{
              width:16, height:16, borderRadius:3, background:c, border:"none",
              cursor:"pointer", flexShrink:0,
            }}/>
        ))}
      </div>
    </div>
  );
}

/* ── Main component ── */
export default function ComplianceDocuments() {
  const { T, theme } = useTheme();
  const catColor = (cat) => {
    const key = CAT_COLOR_KEY[cat];
    if (key === null) return "#FF6B35"; // Grievance Policy — fixed accent, no theme token
    return key ? T[key] : T.muted;
  };
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [catFilter, setCat]       = useState("all");
  const [modal,     setModal]     = useState(null);
  const [current,   setCurrent]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState("");
  const [delCon,    setDel]       = useState(null);
  const editorRef       = useRef(null);
  const pendingContent  = useRef("");

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""), 3500); };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/compliance/?active_only=false`, ax());
      setDocs(r.data||[]);
    } catch { flash("❌ Failed to load documents"); }
    finally { setLoading(false); }
  };
  useEffect(()=>{ load(); },[]);

  // Populate contentEditable editor when modal opens
  useEffect(()=>{
    if((modal==="create"||modal==="edit") && editorRef.current) {
      editorRef.current.innerHTML = pendingContent.current || "";
      // Put cursor at end
      try {
        const range = document.createRange();
        const sel   = window.getSelection();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch(e) { /* ignore */ }
    }
  },[modal]);

  /* Sync contentEditable → form.content */
  const syncContent = () => {
    if(editorRef.current)
      setForm(f=>({...f, content: editorRef.current.innerHTML}));
  };

  const openCreate = () => {
    setForm(EMPTY);
    setCurrent(null);
    pendingContent.current = "";
    setModal("create");
  };

  const openEdit = async (doc) => {
    // Always fetch full content from API (list endpoint may not include full content)
    let fullDoc = doc;
    if(!doc.content) {
      try {
        const r = await axios.get(`${API}/compliance/${doc.id}`, ax());
        fullDoc = r.data;
      } catch { /* use doc as-is */ }
    }
    setForm({
      title:          fullDoc.title          || "",
      category:       fullDoc.category       || "General Policy",
      content:        fullDoc.content        || "",
      summary:        fullDoc.summary        || "",
      version:        fullDoc.version        || "1.0",
      effective_date: fullDoc.effective_date || "",
      is_active:      fullDoc.is_active      !== false,
      is_mandatory:   fullDoc.is_mandatory   === true,
    });
    setCurrent(fullDoc);
    setModal("edit");
    // Store content for editor — will be applied via useEffect after modal mounts
    pendingContent.current = fullDoc.content || "";
  };

  const openView = async (doc) => {
    try {
      const r = await axios.get(`${API}/compliance/${doc.id}`, ax());
      setCurrent(r.data); setModal("view");
    } catch { flash("❌ Failed to load"); }
  };

  const save = async () => {
    const editorHTML = editorRef.current?.innerHTML || "";
    const finalContent = (editorHTML && editorHTML !== "<br>" && editorHTML !== "<br/>")
      ? editorHTML : form.content;
    if(!form.title.trim())    return flash("❌ Title is required");
    if(!finalContent.trim()) return flash("❌ Content is required");
    setSaving(true);
    const payload = {
      title:          form.title,
      category:       form.category,
      content:        finalContent,
      summary:        form.summary  || null,
      version:        form.version  || "1.0",
      effective_date: form.effective_date && form.effective_date.trim()
                        ? form.effective_date : null,
      is_active:      form.is_active,
      is_mandatory:   form.is_mandatory,
    };
    try {
      if(modal==="create") {
        await axios.post(`${API}/compliance/`, payload, ax());
        flash("✅ Document created");
      } else {
        await axios.put(`${API}/compliance/${current.id}`, payload, ax());
        flash("✅ Document updated");
      }
      setModal(null); load();
    } catch(e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d=>d.msg||d.loc?.join(".")||"error").join(", ")
        : detail || "Save failed";
      flash("❌ " + msg);
    }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/compliance/${id}`, ax());
      flash("✅ Removed"); setDel(null); load();
    } catch { flash("❌ Delete failed"); }
  };

  const toggleActive = async (doc) => {
    try {
      await axios.put(`${API}/compliance/${doc.id}`, {
        title:          doc.title,
        category:       doc.category,
        content:        doc.content || "",
        summary:        doc.summary || null,
        version:        doc.version || "1.0",
        effective_date: doc.effective_date || null,
        is_active:      !doc.is_active,
        is_mandatory:   doc.is_mandatory,
      }, ax());
      flash(`✅ ${doc.is_active?"Deactivated":"Activated"}`); load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Update failed")); }
  };

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const filtered = docs.filter(d=>{
    const q=search.toLowerCase();
    return (!search||d.title.toLowerCase().includes(q)||d.category.toLowerCase().includes(q))
      && (catFilter==="all"||d.category===catFilter);
  });

  const grouped = {};
  filtered.forEach(d=>{ (grouped[d.category]||(grouped[d.category]=[])).push(d); });

  const stats = {
    total:     docs.length,
    active:    docs.filter(d=>d.is_active).length,
    mandatory: docs.filter(d=>d.is_mandatory).length,
    cats:      new Set(docs.map(d=>d.category)).size,
  };

  /* ── Shared styles ── */
  const card = {
    background: T.card, border:`1px solid ${T.border}`,
    borderRadius:12, overflow:"hidden",
  };
  const inp = (extra={}) => ({
    width:"100%", padding:"9px 12px",
    border:`1px solid ${T.border}`, borderRadius:8,
    background:T.inp_bg, color:T.text,
    fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none", ...extra,
  });
  const lbl = {
    fontSize:11, fontWeight:700, color:T.muted,
    textTransform:"uppercase", letterSpacing:"0.07em",
    display:"block", marginBottom:5,
  };

  return (
    <div style={{ fontFamily:"Outfit,sans-serif", color:T.text, background:"transparent" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>
            COMPLIANCE & LEGAL
          </div>
          <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px", color:T.text }}>Policy Documents</h2>
          <p style={{ fontSize:13.5, color:T.muted, margin:0 }}>
            Manage HR policies and legal documents visible to employees
          </p>
        </div>
        <button onClick={openCreate} style={{
          display:"flex", alignItems:"center", gap:8, padding:"10px 20px",
          borderRadius:10, border:"none",
          background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
          color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:13,
          fontWeight:800, cursor:"pointer", boxShadow:`0 4px 16px ${T.cyan}4D`,
        }}>
          <Plus size={15}/> New Document
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:24 }}>
        {[
          { label:"Total",     val:stats.total,     color:T.cyan,   icon:<FileText size={17}/> },
          { label:"Active",    val:stats.active,    color:T.green,  icon:<CheckCircle size={17}/> },
          { label:"Mandatory", val:stats.mandatory, color:T.amber,  icon:<AlertTriangle size={17}/> },
          { label:"Categories",val:stats.cats,      color:T.purple, icon:<BookOpen size={17}/> },
        ].map(s=>(
          <div key={s.label} style={{ ...card, padding:"16px 18px",
            borderTop:`3px solid ${s.color}`, display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ color:s.color }}>{s.icon}</div>
            <div>
              <div style={{ fontSize:28, fontWeight:900, color:s.color,
                lineHeight:1, fontFamily:"JetBrains Mono,monospace" }}>{s.val}</div>
              <div style={{ fontSize:11.5, color:T.muted, marginTop:3, textTransform:"uppercase",
                letterSpacing:"0.06em" }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:10, marginBottom:20 }}>
        <div style={{ position:"relative", flex:1 }}>
          <Search size={13} style={{ position:"absolute", left:11, top:11, color:T.muted }}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search documents..."
            style={{ ...inp(), paddingLeft:34 }}/>
        </div>
        <select value={catFilter} onChange={e=>setCat(e.target.value)}
          style={{ ...inp(), width:"auto", cursor:"pointer", paddingRight:24 }}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c=><option key={c}>{c}</option>)}
        </select>
      </div>

      {/* Document Groups */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:T.muted }}>Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign:"center", padding:60, ...card }}>
          <Shield size={44} style={{ color:T.dim, marginBottom:14 }}/>
          <div style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:6 }}>No documents yet</div>
          <div style={{ fontSize:13.5, color:T.muted, marginBottom:20 }}>
            Create your first compliance policy
          </div>
          <button onClick={openCreate} style={{
            padding:"10px 24px", borderRadius:9, border:"none",
            background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
            color:"#fff", fontFamily:"Outfit,sans-serif",
            fontSize:13, fontWeight:800, cursor:"pointer" }}>
            + Create Document
          </button>
        </div>
      ) : Object.entries(grouped).map(([cat, catDocs])=>(
        <div key={cat} style={{ marginBottom:28 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <span style={{ fontSize:18 }}>{CAT_ICONS[cat]||"📄"}</span>
            <span style={{ fontSize:14, fontWeight:800, color:T.text }}>{cat}</span>
            <span style={{ fontSize:11.5, padding:"2px 9px", borderRadius:100,
              background:`${catColor(cat)}18`,
              color:catColor(cat), fontWeight:700 }}>
              {catDocs.length}
            </span>
          </div>
          <div style={{ display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:14 }}>
            {catDocs.map(doc=>(
              <div key={doc.id} style={{
                ...card, padding:"18px 20px",
                opacity:doc.is_active?1:0.55,
                borderLeft:`3px solid ${doc.is_active?catColor(doc.category):T.dim}`,
                transition:"all .2s",
              }}>
                {doc.is_mandatory && (
                  <div style={{ display:"inline-flex", alignItems:"center", gap:4,
                    fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:100,
                    background:`${T.amber}18`, color:T.amber, marginBottom:8 }}>
                    <AlertTriangle size={9}/> MANDATORY
                  </div>
                )}
                <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:6 }}>
                  {doc.title}
                </div>
                {doc.summary && (
                  <div style={{ fontSize:12.5, color:T.muted, lineHeight:1.55,
                    marginBottom:10, display:"-webkit-box", WebkitLineClamp:2,
                    WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                    {doc.summary}
                  </div>
                )}
                <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:14 }}>
                  <span style={{ fontSize:11, padding:"2px 8px", borderRadius:100,
                    background:`${catColor(doc.category)}15`,
                    color:catColor(doc.category), fontWeight:600 }}>
                    v{doc.version}
                  </span>
                  {doc.effective_date && (
                    <span style={{ fontSize:11, padding:"2px 8px", borderRadius:100,
                      background:`${T.cyan}10`, color:T.cyan,
                      display:"flex", alignItems:"center", gap:3 }}>
                      <Clock size={9}/>{doc.effective_date}
                    </span>
                  )}
                  {doc.ack_count>0 && (
                    <span style={{ fontSize:11, padding:"2px 8px", borderRadius:100,
                      background:`${T.green}12`, color:T.green,
                      display:"flex", alignItems:"center", gap:3 }}>
                      <Users size={9}/>{doc.ack_count} read
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", gap:6,
                  borderTop:`1px solid ${T.border}`, paddingTop:12 }}>
                  <button onClick={()=>openView(doc)}
                    style={{ flex:1, padding:"7px", borderRadius:7,
                      border:`1px solid ${T.border}`, background:"none",
                      color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                      fontSize:12, fontWeight:600,
                      display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
                    <Eye size={12}/> View
                  </button>
                  <button onClick={()=>openEdit(doc)}
                    style={{ flex:1, padding:"7px", borderRadius:7,
                      border:`1px solid ${T.cyan}33`, background:"none",
                      color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif",
                      fontSize:12, fontWeight:600,
                      display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
                    <Edit2 size={12}/> Edit
                  </button>
                  <button onClick={()=>toggleActive(doc)}
                    style={{ padding:"7px 10px", borderRadius:7,
                      border:`1px solid ${T.border}`, background:"none",
                      color:doc.is_active?T.amber:T.green, cursor:"pointer" }}>
                    {doc.is_active?<EyeOff size={12}/>:<Eye size={12}/>}
                  </button>
                  <button onClick={()=>setDel(doc)}
                    style={{ padding:"7px 10px", borderRadius:7,
                      border:`1px solid ${T.red}33`,
                      background:"none", color:T.red, cursor:"pointer" }}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ══ CREATE / EDIT MODAL ══ */}
      {(modal==="create"||modal==="edit") && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          backdropFilter:"blur(4px)" }}
          onClick={e=>{if(e.target===e.currentTarget)setModal(null);}}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
            borderRadius:16, width:"100%", maxWidth:760,
            maxHeight:"92vh", overflow:"hidden", display:"flex", flexDirection:"column",
            boxShadow:`0 24px 80px ${theme==="dark"?"rgba(0,0,0,0.6)":"rgba(0,0,0,0.18)"}` }}>

            {/* Modal header */}
            <div style={{ padding:"20px 24px 16px",
              borderBottom:`1px solid ${T.border}`,
              display:"flex", justifyContent:"space-between", alignItems:"center",
              background:`linear-gradient(135deg,${T.cyan}0D,transparent)` }}>
              <div>
                <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
                  letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:3 }}>
                  {modal==="create"?"NEW DOCUMENT":"EDIT DOCUMENT"}
                </div>
                <h3 style={{ fontSize:16, fontWeight:800, margin:0, color:T.text }}>
                  {modal==="create"?"Create Policy Document":"Update Policy Document"}
                </h3>
              </div>
              <button onClick={()=>setModal(null)}
                style={{ border:"none", background:T.toggle_bg,
                  borderRadius:"50%", width:32, height:32, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:T.muted }}>
                <X size={15}/>
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding:24, overflowY:"auto", flex:1 }}>

              {/* Row 1: Title + Category */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
                <div>
                  <label style={lbl}>Document Title *</label>
                  <input value={form.title} onChange={e=>set("title",e.target.value)}
                    placeholder="e.g. Annual Leave Policy" style={inp()}/>
                </div>
                <div>
                  <label style={lbl}>Category *</label>
                  <select value={form.category} onChange={e=>set("category",e.target.value)}
                    style={inp({cursor:"pointer"})}>
                    {CATEGORIES.map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: Summary */}
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Short Summary</label>
                <input value={form.summary} onChange={e=>set("summary",e.target.value)}
                  placeholder="Brief description shown in the document list (optional)"
                  style={inp()}/>
              </div>

              {/* Row 3: Version + Date + Flags */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr",
                gap:14, marginBottom:16 }}>
                <div>
                  <label style={lbl}>Version</label>
                  <input value={form.version} onChange={e=>set("version",e.target.value)}
                    placeholder="1.0" style={inp()}/>
                </div>
                <div>
                  <label style={lbl}>Effective Date</label>
                  <input type="date" value={form.effective_date}
                    onChange={e=>set("effective_date",e.target.value)} style={inp()}/>
                </div>
                <div>
                  <label style={lbl}>Options</label>
                  <div style={{ display:"flex", flexDirection:"column", gap:8, paddingTop:2 }}>
                    {[
                      ["is_mandatory","Mandatory read",   T.amber],
                      ["is_active",   "Active / Published",T.green],
                    ].map(([key,label,color])=>(
                      <label key={key} style={{ display:"flex", alignItems:"center",
                        gap:8, cursor:"pointer", fontSize:13, color:T.muted }}>
                        <div onClick={()=>set(key,!form[key])}
                          style={{ width:36, height:20, borderRadius:100,
                            background:form[key]?color:T.dim, transition:"background .2s",
                            position:"relative", cursor:"pointer", flexShrink:0 }}>
                          <div style={{ position:"absolute", top:2,
                            left:form[key]?18:2, width:16, height:16,
                            borderRadius:"50%", background:"white",
                            transition:"left .2s", boxShadow:"0 1px 4px rgba(0,0,0,0.4)" }}/>
                        </div>
                        <span style={{ color:form[key]?color:T.muted }}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Editor */}
              <div>
                <label style={{...lbl, marginBottom:0}}>Policy Content *</label>
                <div style={{ fontSize:11.5, color:T.dim, marginBottom:8, marginTop:2 }}>
                  Use the toolbar to format headings, bold text, bullet lists and more
                </div>

                {/* Toolbar */}
                <EditorToolbar editorRef={editorRef}/>

                {/* ContentEditable editor */}
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncContent}
                  style={{
                    minHeight:280, padding:"14px 16px",
                    border:`1px solid ${T.border}`, borderRadius:"0 0 8px 8px",
                    background:T.inp_bg, color:T.text, outline:"none",
                    lineHeight:1.75, fontSize:13.5,
                    fontFamily:"Outfit,sans-serif",
                    overflowY:"auto",
                  }}
                  data-placeholder="Start typing your policy content here...

Use headings (H1/H2) for sections, bold for key points, bullet lists for rules."
                />

                <style>{`
                  [contenteditable]:empty:before{
                    content:attr(data-placeholder);
                    color:${T.dim};font-style:italic;pointer-events:none;
                  }
                  [contenteditable] h2{font-size:18px;font-weight:800;color:${T.text};margin:16px 0 6px;}
                  [contenteditable] h3{font-size:15px;font-weight:700;color:${T.cyan};margin:14px 0 4px;}
                  [contenteditable] ul{padding-left:20px;margin:8px 0;}
                  [contenteditable] ol{padding-left:20px;margin:8px 0;}
                  [contenteditable] li{margin:4px 0;color:${T.muted};}
                  [contenteditable] hr{border:none;border-top:1px solid ${T.border};margin:16px 0;}
                  [contenteditable] strong{color:${T.text};}
                  [contenteditable] p{margin:6px 0;}
                `}</style>
              </div>
            </div>

            {/* Modal footer */}
            <div style={{ padding:"16px 24px", borderTop:`1px solid ${T.border}`,
              display:"flex", gap:10, justifyContent:"flex-end",
              background:T.card2 }}>
              <button onClick={()=>setModal(null)}
                style={{ padding:"9px 20px", borderRadius:9,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                style={{ padding:"9px 26px", borderRadius:9, border:"none",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  color:"#fff", cursor:saving?"not-allowed":"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13,
                  fontWeight:800, opacity:saving?.7:1 }}>
                {saving?"Saving...":(modal==="create"?"Create Document":"Save Changes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ VIEW MODAL ══ */}
      {modal==="view" && current && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          backdropFilter:"blur(4px)" }}
          onClick={e=>{if(e.target===e.currentTarget)setModal(null);}}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
            borderRadius:16, width:"100%", maxWidth:760,
            maxHeight:"90vh", overflow:"hidden", display:"flex", flexDirection:"column",
            boxShadow:`0 24px 80px ${theme==="dark"?"rgba(0,0,0,0.6)":"rgba(0,0,0,0.18)"}` }}>

            <div style={{ padding:"20px 24px 14px", borderBottom:`1px solid ${T.border}`,
              display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:22 }}>{CAT_ICONS[current.category]||"📄"}</span>
                  <h3 style={{ fontSize:17, fontWeight:800, margin:0, color:T.text }}>
                    {current.title}
                  </h3>
                </div>
                <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
                  {[
                    {l:current.category, c:catColor(current.category)},
                    {l:`v${current.version}`, c:T.muted},
                    current.effective_date && {l:current.effective_date, c:T.cyan},
                    current.is_mandatory && {l:"Mandatory", c:T.amber},
                  ].filter(Boolean).map(({l,c})=>(
                    <span key={l} style={{ fontSize:11, padding:"3px 9px", borderRadius:100,
                      background:`${c}18`, color:c, fontWeight:600 }}>{l}</span>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>openEdit(current)}
                  style={{ padding:"7px 14px", borderRadius:8,
                    border:`1px solid ${T.cyan}40`, background:"none",
                    color:T.cyan, cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700,
                    display:"flex", alignItems:"center", gap:4 }}>
                  <Edit2 size={11}/> Edit
                </button>
                <button onClick={()=>setModal(null)}
                  style={{ border:"none", background:T.toggle_bg,
                    borderRadius:"50%", width:32, height:32, cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:T.muted }}>
                  <X size={15}/>
                </button>
              </div>
            </div>

            <div style={{ padding:"24px 32px", overflowY:"auto", flex:1 }}>
              {current.summary && (
                <div style={{ padding:"12px 16px",
                  background:`${T.cyan}0F`,
                  border:`1px solid ${T.cyan}26`,
                  borderLeft:`4px solid ${T.cyan}`,
                  borderRadius:10, marginBottom:24,
                  fontSize:13.5, color:T.text, lineHeight:1.65 }}>
                  {current.summary}
                </div>
              )}
              <div
                dangerouslySetInnerHTML={{ __html: current.content }}
                style={{ fontSize:14, lineHeight:1.85, color:T.muted }}
              />
              <style>{`
                .doc-view h2{font-size:18px;font-weight:800;color:${T.text};margin:18px 0 8px;}
                .doc-view h3{font-size:15px;font-weight:700;color:${T.cyan};margin:14px 0 5px;}
                .doc-view ul,.doc-view ol{padding-left:22px;margin:8px 0;}
                .doc-view li{margin:5px 0;color:${T.muted};}
                .doc-view hr{border:none;border-top:1px solid ${T.border};margin:18px 0;}
                .doc-view strong{color:${T.text};}
                .doc-view p{margin:8px 0;}
              `}</style>
              {current.ack_count>0 && (
                <div style={{ marginTop:28, padding:"10px 14px",
                  background:`${T.green}10`, border:`1px solid ${T.green}20`,
                  borderRadius:9, fontSize:12.5, color:T.green,
                  display:"flex", alignItems:"center", gap:6 }}>
                  <CheckCircle size={13}/>
                  {current.ack_count} employee{current.ack_count!==1?"s":" "} acknowledged this document
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ DELETE CONFIRM ══ */}
      {delCon && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setDel(null)}>
          <div style={{ background:T.card, border:`1px solid ${T.red}40`,
            borderRadius:14, width:420, padding:28,
            boxShadow:`0 24px 64px ${theme==="dark"?"rgba(0,0,0,0.5)":"rgba(0,0,0,0.15)"}` }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:22, marginBottom:10 }}>🗑️</div>
            <h3 style={{ fontSize:16, fontWeight:800, color:T.text, marginBottom:8 }}>
              Remove Document?
            </h3>
            <p style={{ fontSize:13.5, color:T.muted, marginBottom:22, lineHeight:1.6 }}>
              This will hide <strong style={{color:T.text}}>{delCon.title}</strong> from employees.
              You can re-activate it later.
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={()=>setDel(null)}
                style={{ padding:"9px 18px", borderRadius:9,
                  border:`1px solid ${T.border}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                Cancel
              </button>
              <button onClick={()=>remove(delCon.id)}
                style={{ padding:"9px 18px", borderRadius:9, border:"none",
                  background:T.red, color:"white", cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%",
          transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")
            ?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow,
          fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}